import type { AccountMetric } from './AccountMetrics';
import { moneyTotalsText, totalsByCurrency, type MoneyTotals } from '../../utils/accountMetrics';
import { toDate } from '../../utils/formatDate';

/**
 * The figures each account-level list shows in its metric row. Every function
 * takes the rows the page is listing (after its own filters) and returns the
 * cards; the page decides when to show them (loaded, not archived, not empty).
 * Money cards are only returned when `showMoney` is true, the same gate the page
 * uses for its money columns, and each currency is added on its own.
 */

const count = <T,>(rows: readonly T[], test: (row: T) => boolean) => rows.filter(test).length;
const distinct = (values: Array<string | null | undefined>) => new Set(values.filter((v): v is string => !!v)).size;

export function vendorMetrics(rows: readonly {
  category: string | null; categories: string[]; rating: number | null; projectId: string | null; projectName: string;
}[]): AccountMetric[] {
  return [
    { key: 'vendors', label: 'Vendors', value: rows.length, icon: 'briefcase', tone: 'pink' },
    { key: 'projects', label: 'Projects covered', value: distinct(rows.map(v => v.projectId ?? v.projectName)), icon: 'folder', tone: 'blue' },
    { key: 'categories', label: 'Categories', value: distinct(rows.flatMap(v => [v.category, ...v.categories])), icon: 'grid', tone: 'purple' },
    { key: 'rated', label: 'Rated', value: count(rows, v => v.rating !== null), icon: 'star', tone: 'orange' },
  ];
}

export function clientMetrics(rows: readonly { project_count: number }[]): AccountMetric[] {
  return [
    { key: 'clients', label: 'Clients', value: rows.length, icon: 'users', tone: 'pink' },
    { key: 'with', label: 'With projects', value: count(rows, c => c.project_count > 0), icon: 'folder', tone: 'blue' },
    { key: 'without', label: 'No project yet', value: count(rows, c => c.project_count === 0), icon: 'clock', tone: 'orange' },
    { key: 'linked', label: 'Projects linked', value: rows.reduce((sum, c) => sum + (c.project_count || 0), 0), icon: 'link', tone: 'teal' },
  ];
}

interface InvoiceMetricRow { status: string; currency: string; amount_paid: number; balance_due: number }
const OWED = ['sent', 'partial', 'overdue'];

export function invoiceMetrics(rows: readonly InvoiceMetricRow[], showMoney: boolean): AccountMetric[] {
  const cards: AccountMetric[] = [
    { key: 'invoices', label: 'Invoices', value: rows.length, icon: 'creditCard', tone: 'pink' },
    { key: 'overdue', label: 'Overdue', value: count(rows, i => i.status === 'overdue'), icon: 'alert', tone: 'red' },
    { key: 'paid', label: 'Paid', value: count(rows, i => i.status === 'paid'), icon: 'checkCircle', tone: 'green' },
  ];
  if (!showMoney) return cards;
  return [
    ...cards,
    {
      key: 'outstanding', label: 'Outstanding', icon: 'dollar', tone: 'orange',
      value: moneyTotalsText(totalsByCurrency(rows.filter(i => OWED.includes(i.status)), i => i.balance_due, i => i.currency)),
      title: 'Balance still due on sent, partly paid and overdue invoices, per currency',
    },
    {
      key: 'collected', label: 'Collected', icon: 'wallet', tone: 'teal',
      value: moneyTotalsText(totalsByCurrency(rows.filter(i => i.status !== 'void'), i => i.amount_paid, i => i.currency)),
      title: 'Payments received on these invoices, per currency',
    },
  ];
}

interface QuoteMetricRow { status: string; currency: string; total: number }

export function quoteMetrics(rows: readonly QuoteMetricRow[], showMoney: boolean): AccountMetric[] {
  const cards: AccountMetric[] = [
    { key: 'quotes', label: 'Quotes', value: rows.length, icon: 'document', tone: 'pink' },
    { key: 'sent', label: 'Awaiting reply', value: count(rows, q => q.status === 'sent'), icon: 'send', tone: 'blue' },
    { key: 'accepted', label: 'Accepted', value: count(rows, q => q.status === 'accepted'), icon: 'checkCircle', tone: 'green' },
    { key: 'closed', label: 'Declined or expired', value: count(rows, q => q.status === 'declined' || q.status === 'expired'), icon: 'x', tone: 'red' },
  ];
  if (!showMoney) return cards;
  return [
    ...cards,
    {
      key: 'accepted-value', label: 'Accepted value', icon: 'dollar', tone: 'teal',
      value: moneyTotalsText(totalsByCurrency(rows.filter(q => q.status === 'accepted'), q => q.total, q => q.currency)),
      title: 'Total of accepted quotes, per currency',
    },
    {
      key: 'sent-value', label: 'Awaiting reply value', icon: 'wallet', tone: 'orange',
      value: moneyTotalsText(totalsByCurrency(rows.filter(q => q.status === 'sent'), q => q.total, q => q.currency)),
      title: 'Total of quotes sent and not yet answered, per currency',
    },
  ];
}

interface ProjectMetricRow {
  project_date: string | null; pipeline_stage: string; readiness: number; contract_value: string | null; contract_currency: string | null;
  /** True/false when the server says whether the project has any tasks; absent or null when it does not say. */
  has_tasks?: boolean | null;
  archived_at?: string | null;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const READINESS_NOTE = 'Average of the projects that have tasks; a project with no tasks is left out.';
const READINESS_UNKNOWN_NOTE = 'n/a: the server does not say which projects have tasks, and some show 0%, so an average would mislead. Shown once it can tell.';

/**
 * Average readiness over projects that have tasks only. A project with no tasks
 * reads 0% but is not "0% ready". Where the server does not say (has_tasks
 * missing), progress above 0% proves tasks exist; 0% does not, so any such
 * project makes the figure "n/a" rather than a guess.
 */
function averageReadiness(rows: readonly ProjectMetricRow[]): { value: string; title: string } {
  const withTasks = rows.filter(p => p.has_tasks === true || (p.has_tasks == null && Number(p.readiness) > 0));
  const cannotTell = rows.some(p => p.has_tasks == null && !(Number(p.readiness) > 0));
  if (cannotTell) return { value: 'n/a', title: READINESS_UNKNOWN_NOTE };
  if (withTasks.length === 0) return { value: 'n/a', title: `${READINESS_NOTE} None of these projects has tasks yet.` };
  const avg = Math.round(withTasks.reduce((sum, p) => sum + (Number(p.readiness) || 0), 0) / withTasks.length);
  return { value: `${avg}%`, title: READINESS_NOTE };
}

/** Projects page: how many, how many are still ahead, how many have no date, how ready they are on average. */
export function projectMetrics(rows: readonly ProjectMetricRow[], today: Date = new Date()): AccountMetric[] {
  const dateOf = (p: ProjectMetricRow) => toDate(p.project_date);
  const startToday = startOfDay(today);
  const upcoming = count(rows, p => {
    if (p.pipeline_stage === 'lost' || p.archived_at) return false;
    const d = dateOf(p);
    return d !== null && startOfDay(d) >= startToday;
  });
  const readiness = averageReadiness(rows);
  return [
    { key: 'projects', label: 'Projects', value: rows.length, icon: 'folder', tone: 'pink' },
    { key: 'upcoming', label: 'Upcoming', value: upcoming, icon: 'calendar', tone: 'blue', title: 'Projects dated today or later, not lost and not archived' },
    { key: 'undated', label: 'No date set', value: count(rows, p => dateOf(p) === null), icon: 'clock', tone: 'orange' },
    { key: 'readiness', label: 'Average readiness', value: readiness.value, icon: 'checkCircle', tone: 'green', title: readiness.title },
  ];
}

/**
 * Contract value per currency, split the way the Pipeline cards show it:
 * open = Lead, Proposal, Booked, Delivering; won = Won. Lost is in neither.
 * Open plus Won is the At a glance "Pipeline Value" (everything not lost).
 */
export function pipelineValueTotals(rows: readonly ProjectMetricRow[]): { open: MoneyTotals; won: MoneyTotals } {
  const sum = (list: readonly ProjectMetricRow[]) => totalsByCurrency(list, p => p.contract_value, p => p.contract_currency);
  return {
    open: sum(rows.filter(p => p.pipeline_stage !== 'won' && p.pipeline_stage !== 'lost')),
    won: sum(rows.filter(p => p.pipeline_stage === 'won')),
  };
}

/** Sales Pipeline page: counts by stage group, and the open and won contract value for roles that may see money. */
export function pipelineMetrics(rows: readonly ProjectMetricRow[], showMoney: boolean): AccountMetric[] {
  const cards: AccountMetric[] = [
    { key: 'projects', label: 'Projects', value: rows.length, icon: 'folder', tone: 'pink' },
    { key: 'enquiries', label: 'Open enquiries', value: count(rows, p => p.pipeline_stage === 'lead' || p.pipeline_stage === 'proposal'), icon: 'inbox', tone: 'blue', title: 'Leads and proposals' },
    { key: 'delivery', label: 'In delivery', value: count(rows, p => p.pipeline_stage === 'booked' || p.pipeline_stage === 'delivering'), icon: 'truck', tone: 'purple', title: 'Booked and delivering' },
    { key: 'won', label: 'Won', value: count(rows, p => p.pipeline_stage === 'won'), icon: 'trophy', tone: 'green' },
  ];
  if (!showMoney) return cards;
  const { open, won } = pipelineValueTotals(rows);
  return [
    ...cards,
    {
      key: 'open-value', label: 'Open contract value', icon: 'dollar', tone: 'teal',
      value: moneyTotalsText(open),
      title: 'Contract value of Lead, Proposal, Booked and Delivering projects, per currency (not Won, not Lost)',
    },
    {
      key: 'won-value', label: 'Won value', icon: 'wallet', tone: 'green',
      value: moneyTotalsText(won),
      title: 'Contract value of Won projects only, per currency. Lost projects are left out of both value cards.',
    },
  ];
}
